import dayjs, { Dayjs } from 'dayjs';
import minMax from 'dayjs/plugin/minMax';

import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { useRunsQuery, useStreamTraceRuns } from '@/hooks/useSwr';
import { GetRunsQueryParams, RunSchema } from '@/types/schema';
import { ensureUTCFormatTimestamp } from '@/utils/ensureUTCFormatTimestamp';
import { utcTime } from '@/utils/utc-time';

import { useChildRunsQueryParams } from './useChildRunsQueryParams';

dayjs.extend(minMax);
export interface UseChildRunsParams {
  sessionId?: string;
  traceId?: string;
  run?: RunSchema;
  rootRun?: RunSchema;
  isEvaluator?: boolean;
  shareToken?: {
    datasetShareToken?: string;
    runShareToken?: string;
  };
  traceMaxStartTime?: string;
  traceMinStartTime?: string;
}

export function useChildRuns({
  sessionId,
  traceId,
  run,
  rootRun,
  isEvaluator,
  shareToken,
  traceMaxStartTime,
  traceMinStartTime,
}: UseChildRunsParams) {
  const { isLoading: isConfigLoading, value: streamingEnabled } = useOrgConfig(
    OrgConfigs.enable_run_tree_streaming
  );

  // Don't make any calls if config is still loading
  const shouldMakeCall = !isConfigLoading;

  const shouldUseStreaming = streamingEnabled && !shareToken;

  // Compute the maximum end time from available sources
  const possibleEndTimes = [
    rootRun?.end_time && utcTime(rootRun.end_time).add(5, 'minute'),
    traceMaxStartTime && utcTime(traceMaxStartTime).add(5, 'minute'),
  ].filter(Boolean) as Dayjs[];

  const endTime = (
    dayjs.max(possibleEndTimes) ?? dayjs().utc().add(5, 'minute')
  )?.toISOString();

  // Prepare streaming query params
  const traceIdForStreaming = rootRun?.id ?? traceId;
  const sessionIdForStreaming = rootRun?.session_id ?? sessionId;

  const streamingParams =
    shouldMakeCall &&
    shouldUseStreaming &&
    traceIdForStreaming &&
    sessionIdForStreaming
      ? {
          trace_id: traceIdForStreaming,
          session_id: sessionIdForStreaming,
          start_time: ensureUTCFormatTimestamp(
            utcTime(rootRun?.start_time ?? traceMinStartTime)
              .subtract(5, 'minute')
              .toISOString()
          ),
          end_time: ensureUTCFormatTimestamp(endTime),
        }
      : null;

  // Prepare legacy query params
  const legacyQuery: GetRunsQueryParams | null = useChildRunsQueryParams({
    sessionId: isEvaluator ? undefined : sessionId,
    traceId,
    run,
    rootRun,
  });

  // Make the appropriate call based on config
  const streamingResult = useStreamTraceRuns(streamingParams, {
    keepPreviousData: true,
  });

  const legacyResult = useRunsQuery(
    shouldMakeCall && (!streamingEnabled || shareToken) ? legacyQuery : null,
    shareToken,
    {
      keepPreviousData: true,
    }
  );

  // Return the appropriate result
  if (isConfigLoading) {
    return {
      data: undefined,
      isLoading: true,
      error: undefined,
      isStreaming: false,
    };
  }

  if (shouldUseStreaming) {
    // There are 4 scenarios to take into account:
    // 1. Load new data: undefined → first frame (show loading)
    // 2. Load new node: previous data → first frame (show loading)
    // 3. Revalidate: previous data → first frame (don't show loading)
    // 4. Load no children: previous data → empty frame (show loading)

    // Show loading only when:
    // - Request parameters changed (switching nodes) AND we're actively loading
    // - OR we have no data at all and are loading (initial load)
    const isInitialLoad = !streamingResult.data && streamingResult.isLoading;
    const isSwitchingNodes =
      streamingResult.data?.runs?.[0]?.trace_id !== traceIdForStreaming &&
      streamingResult.isLoading;
    const isLoadingState = isInitialLoad || isSwitchingNodes;

    return {
      data: streamingResult.data,
      isLoading: isLoadingState,
      error: streamingResult.error,
      isStreaming: true,
    };
  }

  return {
    data: legacyResult.data,
    isLoading: legacyResult.isLoading,
    error: legacyResult.error,
    isStreaming: false,
  };
}
